import { IPathToPageIdMap } from '@move-frontend/contentful-routes';
import DOMPurify from 'isomorphic-dompurify';
import {
	CardSectionDisplay,
	ICardSectionEntry,
} from '../../../../contentful/types/ICardSection';
import { Section } from './Section';
import { ICardItemEntry } from '../../../../contentful/types/ICardItem';
import { getListedEntryKey } from '../../../../utils/getListedEntryKey';
import Card from '../../../../components/common/cards/Card/Card';
import CardsContainer from '../../../../components/common/cards/CardsContainer';
import { getUrlForLink } from '../../../../utils/getUrlForLink';
import { getCardImageSource } from '../../../../utils/getCardImageSource';
import { FEATURE_ENTRYPOINT_TYPE_ID } from '../../../../contentful';
import { useCommonData } from '../../../../common-data/useCommonData';
import Subheading from '../../../../components/common/headings/Subheading';
import { getCardIcon } from '../../../../utils/getCardIcon';
import { H3 } from '../../../../ui';

export interface ICardSectionProps {
	id: string;
	section: ICardSectionEntry;
}

const renderFeatureIcon = ({
	fields: { image, inlineSvg },
}: ICardItemEntry) => {
	if (inlineSvg) {
		return (
			<div
				// eslint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{
					// sanitize the svg content to prevent potential cross-site scripting (XSS) attacks
					// eslint-disable-next-line @typescript-eslint/naming-convention
					__html: DOMPurify.sanitize(inlineSvg, {
						USE_PROFILES: { svg: true },
					}),
				}}
				aria-hidden
			/>
		);
	}

	if (image) {
		return <img src={getCardIcon(image)} alt="" aria-hidden />;
	}

	return undefined;
};

const renderCardItem = (
	item: ICardItemEntry,
	index: number,
	clickable: boolean = false,
	displayAs: CardSectionDisplay,
	pathToPageIdMap: IPathToPageIdMap,
) => {
	const {
		fields: { link, title, image, body },
	} = item;
	const key = getListedEntryKey(item, index);
	const linkTitle = item.fields.link?.fields.title;
	const linkUrl = getUrlForLink(link, pathToPageIdMap);
	const renderBodyAsMarkdown = displayAs !== CardSectionDisplay.Feature;

	let isClickable = clickable;

	if (displayAs === CardSectionDisplay.Feature) {
		return (
			<Card
				icon={renderFeatureIcon(item)}
				imageAlt={title}
				key={key}
				type="feature"
				body={body}
				title={title}
				linkHref={linkUrl}
				linkTitle={linkTitle}
				fullyClickable
			/>
		);
	}

	if (displayAs === CardSectionDisplay.Clickable) {
		isClickable = linkUrl !== undefined;
	}

	return (
		<Card
			key={key}
			title={title}
			imageSource={getCardImageSource(image)}
			linkHref={isClickable ? linkUrl : undefined}
			linkTitle={linkTitle}
			body={body}
			fullyClickable={isClickable}
			imageAlt={title}
			renderBodyAsMarkdown={renderBodyAsMarkdown}
		/>
	);
};

export function CardSection({ id, section }: ICardSectionProps) {
	const { pathToPageIdMap } = useCommonData();
	let displayAs: CardSectionDisplay = CardSectionDisplay.Clickable;
	const { clickable, title, subtitle } = section.fields;

	if (section.contentType === FEATURE_ENTRYPOINT_TYPE_ID) {
		displayAs = CardSectionDisplay.Feature;
	}
	if (!clickable && section.contentType !== FEATURE_ENTRYPOINT_TYPE_ID) {
		displayAs = CardSectionDisplay.NonClickable;
	}
	return (
		<Section id={id}>
			{!title && subtitle && <Subheading type="h3">{subtitle}</Subheading>}

			{title && (
				<H3 styling="h4">
					{subtitle && <Subheading type="h3">{subtitle}</Subheading>}
					{title}
				</H3>
			)}

			<CardsContainer removeCardBorder={!section.fields.clickable}>
				{section.fields.items &&
					section.fields.items.map((item: ICardItemEntry, index: number) =>
						renderCardItem(
							item,
							index,
							section.fields.clickable,
							displayAs,
							pathToPageIdMap,
						),
					)}
			</CardsContainer>
		</Section>
	);
}
